import { useContext, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';

// TODO: replace with the exact hex used by the navbar pills so the page matches the site.
const BRAND = '#A5675B';
const MAX_FILES = 3;
const MAX_MB = 10;

function errorText(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const field = Array.isArray(item.loc) ? item.loc.filter((p) => p !== 'body').join(' ') : '';
        return field ? `${field}: ${item.msg}` : item.msg || String(item);
      })
      .join(' | ');
  }
  return 'Something went wrong. Please try again.';
}

function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

const fmtAadhaar = (n) => String(n || '').replace(/(\d{4})(?=\d)/g, '$1 ');
const fileSize = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

/* ---------- printable application ---------- */

function Row({ label, value }) {
  return (
    <tr>
      <td className="w-2/5 border border-gray-400 px-2 py-1 align-top font-medium">{label}</td>
      <td className="border border-gray-400 px-2 py-1 align-top whitespace-pre-wrap">{value || ''}</td>
    </tr>
  );
}

function PrintSection({ title, children }) {
  return (
    <div className="mt-4 break-inside-avoid">
      <h3 className="bg-gray-100 border border-gray-400 px-2 py-1 text-sm font-semibold uppercase tracking-wide">{title}</h3>
      {children}
    </div>
  );
}

function PrintableApplication({ app, photoUrl }) {
  return (
    <div id="print-area" className="bg-white p-6 text-[13px] leading-snug text-black">
      <div className="flex items-start justify-between gap-4 border-b-2 border-black pb-3">
        <div>
          <h1 className="text-xl font-bold">mybook.rent Scholarship Application</h1>
          <p className="mt-1">Application Number: <strong>{app.application_number}</strong></p>
          <p>Submitted on: {fmtDate(app.submitted_at)}</p>
        </div>
        <div className="h-32 w-24 shrink-0 border border-black overflow-hidden flex items-center justify-center text-xs">
          {photoUrl ? <img src={photoUrl} alt="Applicant" className="h-full w-full object-cover" /> : 'Photo'}
        </div>
      </div>

      <PrintSection title="Personal Details">
        <table className="w-full border-collapse"><tbody>
          <Row label="Full Name" value={app.full_name} />
          <Row label="Date of Birth" value={fmtDate(app.dob)} />
          <Row label="Gender" value={app.gender} />
          <Row label="Aadhaar Number" value={fmtAadhaar(app.aadhaar_number)} />
          <Row label="Phone Number" value={`+91 ${app.phone || ''}`} />
          <Row label="Email Address" value={app.email} />
          <Row label="Home Address" value={app.address} />
        </tbody></table>
      </PrintSection>

      <PrintSection title="Educational Details">
        <table className="w-full border-collapse"><tbody>
          <Row label="Current School or College" value={app.school_name} />
          <Row label="Current Grade / Year of Study" value={app.class_year} />
          <Row label="Degree / Major / Course" value={app.course} />
          <Row label="Student ID Number" value={app.student_id} />
          <Row label="Latest Grade / GPA / Percentage" value={app.marks} />
        </tbody></table>
      </PrintSection>

      <PrintSection title="Family and Financial Details">
        <table className="w-full border-collapse"><tbody>
          <Row label="Parent / Guardian Name" value={app.guardian_name} />
          <Row label="Parent / Guardian Occupation" value={app.guardian_occupation} />
          <Row label="Total Annual Household Income" value={app.family_income != null ? `Rs. ${Number(app.family_income).toLocaleString('en-IN')}` : ''} />
          <Row label="Number of Family Members" value={app.family_members} />
        </tbody></table>
      </PrintSection>

      <PrintSection title="Extracurricular Activities">
        <table className="w-full border-collapse"><tbody>
          <Row label="Clubs / Sports / Leadership Roles" value={app.clubs} />
          <Row label="Community Service / Volunteering" value={app.community_service} />
          <Row label="Awards or Honors Received" value={app.awards} />
        </tbody></table>
      </PrintSection>

      <PrintSection title="Why this scholarship is important and how it will help achieve career goals">
        <p className="border border-gray-400 border-t-0 px-2 py-2 whitespace-pre-wrap">{app.importance}</p>
      </PrintSection>

      <PrintSection title="Review of the book rented from mybook.rent">
        <p className="border border-gray-400 border-t-0 px-2 py-2 whitespace-pre-wrap">{app.review_text}</p>
      </PrintSection>

      <PrintSection title="Application Declaration">
        <div className="border border-gray-400 border-t-0 px-2 py-3">
          <p>I hereby certify that all the information provided above is true, complete and correct to the best of my knowledge.</p>
          <div className="mt-8 flex justify-between gap-6">
            <div className="w-1/2">
              <div className="border-b border-black h-8" />
              <p className="mt-1">Applicant Signature</p>
            </div>
            <div className="w-1/3">
              <div className="border-b border-black h-8" />
              <p className="mt-1">Date: DD/MM/YYYY</p>
            </div>
          </div>
          <div className="mt-8 flex justify-between gap-6">
            <div className="w-1/2">
              <div className="border-b border-black h-8" />
              <p className="mt-1">Parent / Guardian Signature (confirms household income)</p>
            </div>
            <div className="w-1/3">
              <div className="border-b border-black h-8" />
              <p className="mt-1">Date: DD/MM/YYYY</p>
            </div>
          </div>
        </div>
      </PrintSection>

      <PrintSection title="Attestation by School / College (to be filled by the institution)">
        <div className="border border-gray-400 border-t-0 px-2 py-3">
          <p>
            I certify that the applicant named above is a bonafide student of this institution and that the
            school and academic details given above are correct as per our records.
          </p>
          <div className="mt-4 flex gap-4">
            <div className="h-28 w-40 border border-dashed border-black flex items-center justify-center text-xs text-gray-600">
              Official stamp / seal
            </div>
            <div className="flex-1 space-y-6">
              {['Name of Principal / Headmaster / Registrar / HOD', 'Designation', 'Signature', 'Date: DD/MM/YYYY', 'Institution phone / email'].map((l) => (
                <div key={l}>
                  <div className="border-b border-black h-5" />
                  <p className="text-xs mt-0.5">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PrintSection>

      <p className="mt-4 text-xs text-gray-700">
        Return the signed and attested copy to mybook.rent: upload it at mybook.rent/scholarship/attest or post it to
        {' '}{app.postal_address}. Write the application number {app.application_number} on the envelope.
      </p>
    </div>
  );
}

/* ---------- page ---------- */

export default function ScholarshipAttestPage() {
  const { token } = useContext(AuthContext);
  const [params] = useSearchParams();
  const [number, setNumber] = useState((params.get('number') || '').toUpperCase());
  const [app, setApp] = useState(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploaded, setUploaded] = useState(false);
  const fileInput = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = async (num) => {
    setLoading(true);
    setLoadError('');
    try {
      let target = num;
      if (!target) {
        const list = await axios.get(`${API}/scholarship/mine`, { headers: authHeaders });
        const pick = (list.data || []).find((a) => a.status === 'awaiting_attestation') || (list.data || [])[0];
        if (!pick) {
          setLoadError('You have not submitted a scholarship application yet.');
          setLoading(false);
          return;
        }
        target = pick.application_number;
        setNumber(target);
      }
      const res = await axios.get(`${API}/scholarship/mine/${target}`, { headers: authHeaders });
      setApp(res.data);
      if (res.data.photo?.id) {
        try {
          const img = await axios.get(`${API}/scholarship/my-file/${target}/${res.data.photo.id}`, {
            headers: authHeaders,
            responseType: 'blob',
          });
          setPhotoUrl(URL.createObjectURL(img.data));
        } catch {
          setPhotoUrl('');
        }
      }
    } catch (err) {
      setLoadError(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(number);
  }, []);

  useEffect(() => () => photoUrl && URL.revokeObjectURL(photoUrl), [photoUrl]);

  const onPick = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    if (!picked.length) return;
    const okType = (f) => ['application/pdf', 'image/jpeg', 'image/png'].includes(f.type);
    if (picked.some((f) => !okType(f))) {
      setUploadError('Only PDF, JPG or PNG files are allowed.');
      return;
    }
    if (picked.some((f) => f.size > MAX_MB * 1024 * 1024)) {
      setUploadError(`Each file must be ${MAX_MB} MB or smaller.`);
      return;
    }
    if (files.length + picked.length > MAX_FILES) {
      setUploadError(`You can upload up to ${MAX_FILES} files.`);
      return;
    }
    setUploadError('');
    setFiles((cur) => [...cur, ...picked]);
  };

  const upload = async () => {
    if (!files.length) {
      setUploadError('Choose the scan or photo of your attested copy first.');
      return;
    }
    const data = new FormData();
    data.append('application_number', app.application_number);
    files.forEach((f) => data.append('files', f));
    setUploading(true);
    setUploadError('');
    try {
      await axios.post(`${API}/scholarship/attested-copy`, data, { headers: authHeaders });
      setUploaded(true);
      setFiles([]);
      await load(app.application_number);
    } catch (err) {
      setUploadError(errorText(err));
    } finally {
      setUploading(false);
    }
  };

  const awaiting = app?.status === 'awaiting_attestation';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Print only the application sheet */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #print-area, #print-area * { visibility: visible !important; }
          #print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 12mm; }
          @page { size: A4; margin: 8mm; }
        }
      `}</style>

      <div className="print:hidden"><Navbar /></div>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="print:hidden">
          <h1 className="text-3xl font-semibold text-gray-900">Preview and attest your application</h1>

          {loading && <p className="mt-6 text-sm text-gray-600">Loading your application…</p>}
          {loadError && (
            <p className="mt-6 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700" role="alert">
              {loadError} <Link className="underline" to="/scholarship/apply">Go to the application form</Link>
            </p>
          )}

          {app && (
            <>
              <p className="mt-2 text-sm text-gray-600">
                Application number <strong style={{ color: BRAND }}>{app.application_number}</strong> ·{' '}
                Status: <strong>{app.status_label}</strong>
              </p>

              {/* Step 1 */}
              <section className="mt-6 rounded-xl bg-white p-5 shadow-sm border border-gray-100">
                <h2 className="font-semibold text-gray-900">Step 1. Download and print</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Check the preview below. Press the button, then choose <strong>Save as PDF</strong> to keep a PDF, or choose your printer to print it.
                  Your details cannot be changed after submission, so tell us at support@mybook.rent if something is wrong.
                </p>
                <Button
                  type="button"
                  className="mt-3 h-11 px-6 text-white hover:opacity-90"
                  style={{ backgroundColor: BRAND }}
                  onClick={() => window.print()}
                  data-testid="scholarship-print"
                >
                  Download / Print application
                </Button>
              </section>

              {/* Step 2 */}
              <section className="mt-4 rounded-xl bg-white p-5 shadow-sm border border-gray-100">
                <h2 className="font-semibold text-gray-900">Step 2. Sign and get it attested</h2>
                <ul className="mt-1 list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li>You sign and date the declaration. A parent or guardian signs to confirm the household income.</li>
                  <li>Take it to your school or college. The Principal, Headmaster, Registrar or HOD fills the attestation box, signs it and puts the official stamp.</li>
                </ul>
              </section>

              {/* Step 3 */}
              <section className="mt-4 rounded-xl bg-white p-5 shadow-sm border border-gray-100">
                <h2 className="font-semibold text-gray-900">Step 3. Send us the attested copy</h2>

                {awaiting ? (
                  <>
                    <p className="mt-1 text-sm text-gray-700"><strong>Option A. Upload</strong> a scan or a clear photo (PDF, JPG or PNG, up to {MAX_FILES} files, {MAX_MB} MB each). Include every page.</p>
                    <input ref={fileInput} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" onChange={onPick} />
                    <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => fileInput.current?.click()} disabled={files.length >= MAX_FILES}>
                      {files.length ? 'Add more files' : 'Choose files'}
                    </Button>
                    {files.length > 0 && (
                      <ul className="mt-2 divide-y divide-gray-100 rounded-md border border-gray-200 text-sm">
                        {files.map((f, i) => (
                          <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2">
                            <span className="truncate">{f.name} <span className="text-xs text-gray-500">({fileSize(f.size)})</span></span>
                            <button type="button" className="text-lg leading-none text-gray-400 hover:text-red-600" aria-label={`Remove ${f.name}`} onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))}>×</button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {uploadError && <p className="mt-2 text-sm text-red-600" role="alert">{uploadError}</p>}
                    <Button
                      type="button"
                      className="mt-3 h-11 px-6 text-white hover:opacity-90"
                      style={{ backgroundColor: BRAND }}
                      disabled={uploading || !files.length}
                      onClick={upload}
                      data-testid="scholarship-upload-attested"
                    >
                      {uploading ? 'Uploading…' : 'Upload attested copy'}
                    </Button>

                    <p className="mt-5 text-sm text-gray-700"><strong>Option B. Post the original</strong> to:</p>
                    <p className="mt-1 rounded-md bg-gray-50 border border-gray-200 p-3 text-sm">{app.postal_address}</p>
                    <p className="mt-1 text-xs text-gray-500">Write the application number {app.application_number} on the envelope. We will update your status when it arrives.</p>
                  </>
                ) : (
                  <p className="mt-1 text-sm text-green-700" data-testid="scholarship-attested-received">
                    {uploaded || app.attested_received
                      ? `We have received your attested copy${app.attested_via === 'post' ? ' by post' : ''}. It is now in the review queue.`
                      : 'This application is no longer waiting for an attested copy.'}
                  </p>
                )}
              </section>

              <div className="mt-4">
                <Button asChild variant="outline">
                  <Link to={`/scholarship/status?number=${app.application_number}`}>Track status</Link>
                </Button>
              </div>

              <h2 className="mt-8 mb-2 text-sm font-semibold text-gray-700">Application preview</h2>
            </>
          )}
        </div>

        {app && (
          <div className="overflow-x-auto rounded-md border border-gray-300 bg-white print:border-0">
            <PrintableApplication app={app} photoUrl={photoUrl} />
          </div>
        )}
      </main>
    </div>
  );
}
